import axios from 'axios'
import type { PagedResult } from './contractorService'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const buildUrl = (path: string) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL.replace(/\/$/, '')}${path}`
  }
  return path
}

const isGuidString = (value?: string | null): value is string => {
  if (typeof value !== 'string') {
    return false
  }
  const trimmed = value.trim()
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(trimmed)
}

// REST client for permit creation and retrieval flows used across the permit-request feature.
export interface PermitFieldValuePayload {
  fieldId: string
  fieldCode: string
  fieldType: string
  sectionCode: string
  valueText?: string | null
  valueNumber?: number | null
  valueBoolean?: boolean | null
  valueDate?: string | null
  valueTimestamp?: string | null
  displayValue?: string | null
}

export interface PermitRiskAssessmentPayload {
  personScore: number
  communityScore: number
  environmentScore: number
  assetScore: number
  quantitativeScore: number
  qualitativeScore: number
  consequenceScore: number
  likelihoodScore: number
  riskScore: number
  riskLevel: string
}

export interface WorkAreaMarkerPayload {
  xPercent: number
  yPercent: number
}

export interface WorkAreaRectanglePayload {
  xPercent: number
  yPercent: number
  widthPercent: number
  heightPercent: number
}

export interface WorkAreaGpsAreaPayload {
  north: number
  south: number
  east: number
  west: number
}

export interface WorkAreaPayload {
  mapMode: string
  plotPlanId?: string | null
  plotPlanVersionId?: string | null
  plotPlanName?: string | null
  plotPlanImagePath?: string | null
  marker?: WorkAreaMarkerPayload | null
  area?: WorkAreaRectanglePayload | null
  areas?: WorkAreaRectanglePayload[] | null
  gpsLatitude?: number | null
  gpsLongtitude?: number | null
  gpsAreas?: WorkAreaGpsAreaPayload[] | null
}

export interface PermitAttachmentPayload {
  documentTypeId?: string | null
  documentCode?: string | null
  fileName: string
  storagePath: string
  uploadedBy?: string | null
  uploadedAt?: string | null
  status?: string | null
}

export interface UploadAttachmentResponse {
  fileName: string
  storedFileName: string
  storagePath: string
  contentType: string
  size: number
  documentCode?: string | null
}

export interface CreatePermitRequestPayload {
  tenantId: string
  templateId: string
  templateVersionId: string
  permitTypeCode: string
  permitNumber: string
  title: string
  requesterId?: string | null
  requesterName?: string | null
  requesterCompany?: string | null
  companyId?: string | null
  siteId?: string | null
  plannedStart?: string | null
  plannedEnd?: string | null
  supervisorEmployeeId?: string | null
  metadata?: Record<string, unknown> | null
  fieldValues: PermitFieldValuePayload[]
  workers?: PermitWorkerSelectionPayload[]
  subActivities?: CreatePermitSubActivitySelection[]
  riskAssessment?: PermitRiskAssessmentPayload | null
  workArea?: WorkAreaPayload | null
  attachments?: PermitAttachmentPayload[]
}

export interface CreatePermitResponsePayload {
  permitId: string
  permitNumber: string
  status: string
}

export interface UpdatePermitRequestPayload extends CreatePermitRequestPayload {
  permitId: string
}

export interface CreatePermitSubActivityFieldValue {
  fieldId: string
  valueText?: string | null
}

export interface CreatePermitSubActivitySelection {
  subactivityId: string
  fieldValues?: CreatePermitSubActivityFieldValue[]
}

export interface PermitListItem {
  permitId: string
  permitNumber: string
  title: string
  status: string
  statusName?: string | null
  plannedStart?: string | null
  plannedEnd?: string | null
  createdAt?: string | null
  permitType: string
  workDescription: string
  location: string
  area?: string | null
  locationName?: string | null
  unit?: string | null
  unitName?: string | null
  requesterId?: string | null
  supervisorEmployeeId?: string | null
  requesterName: string
  requesterCompany: string
  approvedBy: string
  riskLevel: string
}

export interface OpenPermitSupervisorAssignment {
  employeeId: string
  employeeName: string
  position?: string | null
  department?: string | null
  startTime?: string | null
  endTime?: string | null
}

export interface OpenPermitSubActivity {
  subactivityId: string
  code: string
  name: string
}

export interface OpenPermitListItem {
  permitId: string
  permitNumber: string
  permitTypeCode?: string | null
  permitType: string
  title: string
  status: string
  statusName?: string | null
  plannedStart?: string | null
  plannedEnd?: string | null
  workDescription: string
  location: string
  area?: string | null
  unit?: string | null
  requesterName: string
  requesterCompany: string
  requesterPhone?: string | null
  requesterEmail?: string | null
  workersCount: number
  riskLevel: string
  priority?: string | null
  approvedBy?: string | null
  approvedAt?: string | null
  openedByName?: string | null
  openedAt?: string | null
  closedByName?: string | null
  closedAt?: string | null
  areaOwner?: string | null
  selectedSubActivities?: OpenPermitSubActivity[] | null
  supervisors: OpenPermitSupervisorAssignment[]
}

export interface OpenPermitSupervisorPayload {
  employeeId: string
  startTime?: string | null
  endTime?: string | null
}

export interface OpenPermitRequestPayload {
  tenantId: string
  permitId: string
  activeRoleId?: string | null
  openedByUserId?: string | null
  notes?: string | null
  supervisors: OpenPermitSupervisorPayload[]
}

export interface OpenPermitBulkRequestPayload {
  tenantId: string
  permitIds: string[]
  activeRoleId?: string | null
  openedByUserId?: string | null
  notes?: string | null
  supervisors: OpenPermitSupervisorPayload[]
}

export interface CancelPermitRequestPayload {
  tenantId: string
  permitId: string
  cancelledBy?: string | null
  reasonCode: string
  reasonDetail?: string | null
}

export interface CancelPermitBulkRequestPayload {
  tenantId: string
  permitIds: string[]
  cancelledBy?: string | null
  reasonCode: string
  reasonDetail?: string | null
}

export interface BulkPermitActionResult {
  requestedCount: number
  processedCount: number
  permitIds: string[]
}

export interface CancelReasonOption {
  code: string
  description: string
  itemOrder?: number | null
}

export interface PermitEmployeeLookup {
  employeeId: string
  userId?: string | null
  employeeName: string
  position?: string | null
  department?: string | null
  email?: string | null
  phone?: string | null
}

export interface ClosePermitChecklistItemPayload {
  itemCode: string
  itemName: string
  required: boolean
  isPassed: boolean
  notes?: string | null
}

export interface ClosePermitRequestPayload {
  tenantId: string
  permitId: string
  activeRoleId?: string | null
  closedByUserId?: string | null
  actualStart?: string | null
  actualEnd?: string | null
  summary?: string | null
  safetyObservation?: string | null
  templateId?: string | null
  templateName?: string | null
  checklist: ClosePermitChecklistItemPayload[]
}

export interface ClosePermitBulkRequestPayload {
  tenantId: string
  permitIds: string[]
  activeRoleId?: string | null
  closedByUserId?: string | null
  actualStart?: string | null
  actualEnd?: string | null
  summary?: string | null
  safetyObservation?: string | null
  templateId?: string | null
  templateName?: string | null
  checklist: ClosePermitChecklistItemPayload[]
}

export interface ChangePermitWorkTimeRequestPayload {
  tenantId: string
  permitId: string
  requestedByUserId?: string | null
  newWorkStart: string
  newWorkEnd: string
  reason: string
}

export interface ExtendPermitRequestPayload {
  tenantId: string
  permitId: string
  requestedByUserId?: string | null
  newExtendedEnd: string
  reason: string
}

export interface PermitDetailFieldValue {
  fieldId: string
  fieldCode: string
  sectionCode: string
  fieldDataType: string
  valueText?: string | null
  valueNumber?: number | null
  valueBoolean?: boolean | null
  valueDate?: string | null
  valueTimestamp?: string | null
  displayValue?: string | null
}

export interface PermitWorkerSelectionPayload {
  workerId: string
  companyId?: string | null
  workerName?: string | null
  workerPosition?: string | null
  roleInPermit?: string | null
}

export interface PermitDetailWorker {
  permitWorkerId: string
  workerId: string
  companyId: string
  fullName: string
  companyName: string
  company?: string | null
  orgName?: string | null
  position?: string | null
  username?: string | null
  jobRole: string
  status: string
  cardNumber?: string | null
  imageType?: string | null
  imagePath?: string | null
  roleInPermit?: string | null
  createdAt: string
}

export interface PermitAttachmentDetail {
  permitAttachmentId: string
  documentTypeId: string
  documentCode: string
  documentName: string
  fileName: string
  storedFileName?: string | null
  storagePath: string
  fileSize?: number | null
  uploadedBy?: string | null
  uploadedByName?: string | null
  uploadedAt: string
  status: string
}

export interface PermitDetailSubactivityFieldValue {
  fieldId: string
  code: string
  label: string
  fieldType: string
  required: boolean
  valueText?: string | null
}

export interface PermitDetailSelectedSubActivity {
  subactivityId: string
  code: string
  name: string
  description?: string | null
  selectedAt: string
  fieldValues: PermitDetailSubactivityFieldValue[]
}

export interface PermitRiskAssessmentDetail extends PermitRiskAssessmentPayload {
  createdAt: string
}

export interface SubmitPermitWorkflowResponse {
  tenantId: string
  permitId: string
  permitWorkflowInstanceId: string
  policyWarnings?: PermitSubmitPolicyMessage[] | null
  emailErrorMessage?: string | null
}

export interface PermitSubmitPolicyMessage {
  ruleId?: string | null
  ruleType: string
  severity: string
  message: string
  code: string
}

export interface PermitSubmitPolicyValidationResult {
  canSubmit: boolean
  policyFound: boolean
  policyId?: string | null
  errors: PermitSubmitPolicyMessage[]
  warnings: PermitSubmitPolicyMessage[]
}

export interface PermitDetail {
  permitId: string
  tenantId: string
  permitNumber: string
  status: string
  statusName?: string | null
  title: string
  templateId?: string | null
  templateVersionId: string
  permitTypeCode: string
  permitTypeName: string
  plannedStart?: string | null
  plannedEnd?: string | null
  supervisorEmployeeId?: string | null
  requesterId: string
  requesterName?: string | null
  requesterCompany?: string | null
  siteId?: string | null
  approvalStateId?: string | null
  metadata: Record<string, string | null>
  attachments: PermitAttachmentDetail[]
  fieldValues: PermitDetailFieldValue[]
  workers: PermitDetailWorker[]
  selectedSubActivities: PermitDetailSelectedSubActivity[]
  riskAssessment?: PermitRiskAssessmentDetail | null
  workArea?: WorkAreaPayload | null
}

export interface PermitWorkflowStageTimeline {
  permitApprovalId: string
  stageId: string
  seqIndex: number
  code?: string | null
  name?: string | null
  status: string
  assignedRoleId?: string | null
  assignedRoleCode?: string | null
  assignedRoleName?: string | null
  assignedRoleUserNames?: string | null
  assignedUserId?: string | null
  assignedUserName?: string | null
  assignedUserCompanyId?: string | null
  onMissingApprover?: 'FAIL' | 'SKIP' | null
  startedAt?: string | null
  completedAt?: string | null
}

export interface PermitWorkflowHistoryItem {
  permitWorkflowStageHistoryId: string
  permitWorkflowInstanceId: string
  isCurrentInstance: boolean
  permitApprovalId?: string | null
  stageId: string
  seqIndex: number
  statusBefore?: string | null
  statusAfter: string
  actedByUserId?: string | null
  actedByUserName?: string | null
  actedByRoleId?: string | null
  actedByRoleName?: string | null
  actionNote?: string | null
  actedAt: string
}

export interface PermitExtensionHistoryItem {
  requestedAt: string
  requestedByUserId?: string | null
  requestedByUserName?: string | null
  reason?: string | null
  previousEnd?: string | null
  newExtendedEnd?: string | null
}

export interface PermitWorkflowTimeline {
  permitId: string
  permitWorkflowInstanceId: string
  workflowVersionId: string
  status: string
  startedAt: string
  completedAt?: string | null
  stages: PermitWorkflowStageTimeline[]
  history: PermitWorkflowHistoryItem[]
  extensionHistory: PermitExtensionHistoryItem[]
}

// Minimal user lookup for displaying actor names in workflow/cancel summaries
export interface UserSummary {
  userId: string
  fullName?: string | null
  userName?: string | null
  email?: string | null
  employeeId?: string | null
}

export interface DocumentSummary {
  documentId: string
  code: string
  name: string
  description?: string | null
}

export async function createPermit(
  payload: CreatePermitRequestPayload,
): Promise<CreatePermitResponsePayload> {
  const response = await axios.post<CreatePermitResponsePayload>(buildUrl('/api/v1/permits'), payload)
  return response.data
}

export interface PermitListQuery {
  tenantId: string
  permitNumber?: string | null
  requesterId?: string | null
  requesterOnly?: boolean
  siteId?: string | null
  companyId?: string | null
  locationId?: string | null
  companyIds?: string[]
  locationIds?: string[]
  statuses?: string[]
  permitTypes?: string[]
  assignedToUserId?: string | null
  assignedRoleCode?: string | null
  status?: string | null
  permitType?: string | null
  searchTerm?: string | null
  plannedStartFrom?: string | null
  plannedStartTo?: string | null
  offHoursOnly?: boolean
  projectNo?: string | null
  workOrderNo?: string | null
  inertGasArea?: string | null
  toolType?: string | null
  subactivityCodes?: string[]
  pageNumber?: number
  pageSize?: number
}

export interface SubactivityOption {
  code: string
  name: string
}

export interface ProjectWorkOrderOption {
  value: string
  label: string
}

export interface OverallDashboardSummary {
  totalPermit: number
  totalWorker: number
  coldWork: number
  confineWork: number
  confineWorkInertGasArea: number
  confineWorkNonInertGasArea: number
  hotWork: number
  hotWorkOpenFire: number
  hotWorkNonOpenFire: number
  waitForOpen: number
  opened: number
  closed: number
  cancelled: number
}

export interface PendingApprovalsQuery {
  tenantId: string
  assignedToUserId?: string | null
  assignedRoleIds?: string[]
  companyIds?: string[]
  locationIds?: string[]
  statuses?: string[]
  permitTypes?: string[]
  searchTerm?: string | null
  plannedStartFrom?: string | null
  plannedStartTo?: string | null
  sortField?: string | null
  sortDirection?: "asc" | "desc" | null
  pageNumber?: number
  pageSize?: number
}

export interface OpenPermitListQuery {
  tenantId: string
  activeRoleId?: string | null
  siteId?: string | null
  locationIds?: string[]
  area?: string | null
  permitType?: string | null
  status?: string | null
  searchTerm?: string | null
  plannedStartFrom?: string | null
  plannedStartTo?: string | null
  page?: number
  pageSize?: number
}

export async function getPermits(query: PermitListQuery): Promise<PagedResult<PermitListItem>> {
  const params: Record<string, string | string[]> = {
    tenantId: query.tenantId,
  }

  if (query.permitNumber) {
    params.permitNumber = query.permitNumber
  }
  if (query.requesterId && isGuidString(query.requesterId)) {
    params.requesterId = query.requesterId
  }
  if (query.requesterOnly) {
    params.requesterOnly = "true"
  }
  if (query.siteId) {
    params.siteId = query.siteId
  }

  if (query.companyId && isGuidString(query.companyId)) {
    params.companyId = query.companyId
  }
  if (Array.isArray(query.companyIds) && query.companyIds.length > 0) {
    params.companyIds = query.companyIds.filter(isGuidString)
  }
  if (query.locationId && isGuidString(query.locationId)) {
    params.locationId = query.locationId
  }
  if (Array.isArray(query.locationIds) && query.locationIds.length > 0) {
    params.locationIds = query.locationIds.filter(isGuidString)
  }
  if (query.assignedToUserId && isGuidString(query.assignedToUserId)) {
    params.assignedToUserId = query.assignedToUserId
  }
  if (query.assignedRoleCode) {
    params.assignedRoleCode = query.assignedRoleCode
  }

  if (Array.isArray(query.statuses) && query.statuses.length > 0) {
    params.statuses = query.statuses
  } else if (query.status) {
    params.status = query.status
  }

  if (Array.isArray(query.permitTypes) && query.permitTypes.length > 0) {
    params.permitTypes = query.permitTypes
  } else if (query.permitType) {
    params.permitType = query.permitType
  }

  if (query.searchTerm) {
    params.searchTerm = query.searchTerm
  }

  if (query.plannedStartFrom) {
    params.plannedStartFrom = query.plannedStartFrom
  }

  if (query.plannedStartTo) {
    params.plannedStartTo = query.plannedStartTo
  }

  if (typeof query.pageNumber === 'number') {
    params.pageNumber = String(query.pageNumber)
  }

  if (typeof query.pageSize === 'number') {
    params.pageSize = String(query.pageSize)
  }

  const response = await axios.get<PagedResult<PermitListItem>>(buildUrl('/api/v1/permits'), {
    params,
    paramsSerializer: (requestParams) => {
      const search = new URLSearchParams()
      Object.entries(requestParams).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return
        }
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              search.append(key, String(item))
            }
          })
          return
        }
        search.append(key, String(value))
      })
      return search.toString()
    },
  })

  return response.data ?? {
    items: [],
    pageNumber: 1,
    pageSize: Number(params.pageSize ?? '0') || 0,
    totalItems: 0,
    totalPages: 0,
  }
}

export async function getOverallDashboardPermits(query: PermitListQuery): Promise<PagedResult<PermitListItem>> {
  const params: Record<string, string | string[]> = {
    tenantId: query.tenantId,
  }

  if (query.requesterId && isGuidString(query.requesterId)) {
    params.requesterId = query.requesterId
  }
  if (query.siteId) {
    params.siteId = query.siteId
  }

  if (query.companyId && isGuidString(query.companyId)) {
    params.companyId = query.companyId
  }
  if (Array.isArray(query.companyIds) && query.companyIds.length > 0) {
    params.companyIds = query.companyIds.filter(isGuidString)
  }
  if (query.locationId && isGuidString(query.locationId)) {
    params.locationId = query.locationId
  }
  if (Array.isArray(query.locationIds) && query.locationIds.length > 0) {
    params.locationIds = query.locationIds.filter(isGuidString)
  }
  if (query.assignedToUserId && isGuidString(query.assignedToUserId)) {
    params.assignedToUserId = query.assignedToUserId
  }
  if (query.assignedRoleCode) {
    params.assignedRoleCode = query.assignedRoleCode
  }

  if (Array.isArray(query.statuses) && query.statuses.length > 0) {
    params.statuses = query.statuses
  } else if (query.status) {
    params.status = query.status
  }

  if (Array.isArray(query.permitTypes) && query.permitTypes.length > 0) {
    params.permitTypes = query.permitTypes
  } else if (query.permitType) {
    params.permitType = query.permitType
  }

  if (query.searchTerm) {
    params.searchTerm = query.searchTerm
  }

  if (query.plannedStartFrom) {
    params.plannedStartFrom = query.plannedStartFrom
  }

  if (query.plannedStartTo) {
    params.plannedStartTo = query.plannedStartTo
  }

  if (query.offHoursOnly) {
    params.offHoursOnly = 'true'
  }

  if (query.projectNo) {
    params.projectNo = query.projectNo
  }

  if (query.workOrderNo) {
    params.workOrderNo = query.workOrderNo
  }

  if (query.inertGasArea) {
    params.inertGasArea = query.inertGasArea
  }

  if (query.toolType) {
    params.toolType = query.toolType
  }

  if (Array.isArray(query.subactivityCodes) && query.subactivityCodes.length > 0) {
    params.subactivityCodes = query.subactivityCodes
  }

  if (typeof query.pageNumber === 'number') {
    params.pageNumber = String(query.pageNumber)
  }

  if (typeof query.pageSize === 'number') {
    params.pageSize = String(query.pageSize)
  }

  const response = await axios.get<PagedResult<PermitListItem>>(buildUrl('/api/v1/permits/overall-dashboard'), {
    params,
    paramsSerializer: (requestParams) => {
      const search = new URLSearchParams()
      Object.entries(requestParams).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return
        }
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              search.append(key, String(item))
            }
          })
          return
        }
        search.append(key, String(value))
      })
      return search.toString()
    },
  })

  return response.data ?? {
    items: [],
    pageNumber: 1,
    pageSize: Number(params.pageSize ?? '0') || 0,
    totalItems: 0,
    totalPages: 0,
  }
}

export async function getOverallDashboardSummary(query: PermitListQuery): Promise<OverallDashboardSummary> {
  const params: Record<string, string | string[]> = {
    tenantId: query.tenantId,
  }

  if (query.requesterId && isGuidString(query.requesterId)) {
    params.requesterId = query.requesterId
  }
  if (query.siteId) {
    params.siteId = query.siteId
  }
  if (query.companyId && isGuidString(query.companyId)) {
    params.companyId = query.companyId
  }
  if (Array.isArray(query.companyIds) && query.companyIds.length > 0) {
    params.companyIds = query.companyIds.filter(isGuidString)
  }
  if (query.locationId && isGuidString(query.locationId)) {
    params.locationId = query.locationId
  }
  if (Array.isArray(query.locationIds) && query.locationIds.length > 0) {
    params.locationIds = query.locationIds.filter(isGuidString)
  }

  if (Array.isArray(query.statuses) && query.statuses.length > 0) {
    params.statuses = query.statuses
  } else if (query.status) {
    params.status = query.status
  }

  if (Array.isArray(query.permitTypes) && query.permitTypes.length > 0) {
    params.permitTypes = query.permitTypes
  } else if (query.permitType) {
    params.permitType = query.permitType
  }

  if (query.searchTerm) {
    params.searchTerm = query.searchTerm
  }

  if (query.plannedStartFrom) {
    params.plannedStartFrom = query.plannedStartFrom
  }

  if (query.plannedStartTo) {
    params.plannedStartTo = query.plannedStartTo
  }

  if (query.offHoursOnly) {
    params.offHoursOnly = 'true'
  }

  if (query.projectNo) {
    params.projectNo = query.projectNo
  }

  if (query.workOrderNo) {
    params.workOrderNo = query.workOrderNo
  }

  if (Array.isArray(query.subactivityCodes) && query.subactivityCodes.length > 0) {
    params.subactivityCodes = query.subactivityCodes
  }

  const response = await axios.get<OverallDashboardSummary>(buildUrl('/api/v1/permits/overall-dashboard-summary'), {
    params,
    paramsSerializer: (requestParams) => {
      const search = new URLSearchParams()
      Object.entries(requestParams).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return
        }
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              search.append(key, String(item))
            }
          })
          return
        }
        search.append(key, String(value))
      })
      return search.toString()
    },
  })

  return (
    response.data ?? {
      totalPermit: 0,
      coldWork: 0,
      confineWork: 0,
      confineWorkInertGasArea: 0,
      confineWorkNonInertGasArea: 0,
      hotWork: 0,
      hotWorkOpenFire: 0,
      hotWorkNonOpenFire: 0,
      waitForOpen: 0,
      opened: 0,
      closed: 0,
      cancelled: 0,
    }
  )
}

export interface OverallDashboardWorkerItem {
  workerId: string
  workerName: string
  workerCompany?: string | null
  workerRole?: string | null
  area: string
  permitId: string
  permitNumber: string
  status: string
  statusName?: string | null
}

export async function getOverallDashboardWorkers(query: PermitListQuery): Promise<OverallDashboardWorkerItem[]> {
  const params: Record<string, string | string[]> = {
    tenantId: query.tenantId,
  }

  if (query.siteId) {
    params.siteId = query.siteId
  }
  if (query.companyId && isGuidString(query.companyId)) {
    params.companyId = query.companyId
  }
  if (Array.isArray(query.companyIds) && query.companyIds.length > 0) {
    params.companyIds = query.companyIds.filter(isGuidString)
  }
  if (query.locationId && isGuidString(query.locationId)) {
    params.locationId = query.locationId
  }
  if (Array.isArray(query.locationIds) && query.locationIds.length > 0) {
    params.locationIds = query.locationIds.filter(isGuidString)
  }

  if (Array.isArray(query.statuses) && query.statuses.length > 0) {
    params.statuses = query.statuses
  } else if (query.status) {
    params.status = query.status
  }

  if (Array.isArray(query.permitTypes) && query.permitTypes.length > 0) {
    params.permitTypes = query.permitTypes
  } else if (query.permitType) {
    params.permitType = query.permitType
  }

  if (query.searchTerm) {
    params.searchTerm = query.searchTerm
  }

  if (query.plannedStartFrom) {
    params.plannedStartFrom = query.plannedStartFrom
  }

  if (query.plannedStartTo) {
    params.plannedStartTo = query.plannedStartTo
  }

  if (query.offHoursOnly) {
    params.offHoursOnly = 'true'
  }

  if (query.projectNo) {
    params.projectNo = query.projectNo
  }

  if (query.workOrderNo) {
    params.workOrderNo = query.workOrderNo
  }

  if (query.inertGasArea) {
    params.inertGasArea = query.inertGasArea
  }

  if (query.toolType) {
    params.toolType = query.toolType
  }

  if (Array.isArray(query.subactivityCodes) && query.subactivityCodes.length > 0) {
    params.subactivityCodes = query.subactivityCodes
  }

  const response = await axios.get<OverallDashboardWorkerItem[]>(buildUrl('/api/v1/permits/overall-dashboard-workers'), {
    params,
    paramsSerializer: (requestParams) => {
      const search = new URLSearchParams()
      Object.entries(requestParams).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return
        }
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              search.append(key, String(item))
            }
          })
          return
        }
        search.append(key, String(value))
      })
      return search.toString()
    },
  })

  return response.data ?? []
}

export async function getSubactivityOptions(): Promise<SubactivityOption[]> {
  const response = await axios.get<SubactivityOption[]>(buildUrl('/api/v1/permits/overall-dashboard-subactivities'))
  return response.data ?? []
}

export async function getProjectNoOptions(search: string): Promise<ProjectWorkOrderOption[]> {
  const response = await axios.get<ProjectWorkOrderOption[]>(buildUrl('/api/v1/permits/overall-dashboard-project-numbers'), {
    params: { search },
  })
  return response.data ?? []
}

export async function getWorkOrderNoOptions(search: string): Promise<ProjectWorkOrderOption[]> {
  const response = await axios.get<ProjectWorkOrderOption[]>(buildUrl('/api/v1/permits/overall-dashboard-work-order-numbers'), {
    params: { search },
  })
  return response.data ?? []
}

export async function getPendingApprovals(query: PendingApprovalsQuery): Promise<PagedResult<PermitListItem>> {
  const params: Record<string, string> = {
    tenantId: query.tenantId,
  }

  if (query.assignedToUserId && isGuidString(query.assignedToUserId)) {
    params.assignedToUserId = query.assignedToUserId
  }

  const companyIds = (query.companyIds ?? []).filter((id) => isGuidString(id))
  const locationIds = (query.locationIds ?? []).filter((id) => isGuidString(id))
  const statuses = (query.statuses ?? []).filter((status) => status && status.toUpperCase() !== "ALL")
  const permitTypes = (query.permitTypes ?? []).filter((type) => type && type.toUpperCase() !== "ALL")

  if (query.searchTerm) {
    const trimmed = query.searchTerm.trim()
    if (trimmed) {
      params.searchTerm = trimmed
    }
  }

  if (query.plannedStartFrom) {
    params.plannedStartFrom = query.plannedStartFrom
  }

  if (query.plannedStartTo) {
    params.plannedStartTo = query.plannedStartTo
  }

  if (query.sortField) {
    const trimmed = query.sortField.trim()
    if (trimmed) {
      params.sortField = trimmed
    }
  }

  if (query.sortDirection) {
    params.sortDirection = query.sortDirection
  }

  const roleIds = (query.assignedRoleIds ?? []).filter((id) => isGuidString(id))

  if (typeof query.pageNumber === 'number') {
    params.pageNumber = String(query.pageNumber)
  }

  if (typeof query.pageSize === 'number') {
    params.pageSize = String(query.pageSize)
  }

  const response = await axios.get<PagedResult<PermitListItem>>(buildUrl('/api/v1/permits/pending-approvals'), {
    params: {
      ...params,
      assignedRoleIds: roleIds,
      companyIds,
      locationIds,
      statuses,
      permitTypes,
    },
    paramsSerializer: (requestParams) => {
      const search = new URLSearchParams()
      Object.entries(requestParams).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return
        }
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              search.append(key, String(item))
            }
          })
          return
        }
        search.append(key, String(value))
      })
      return search.toString()
    },
  })

  return (
    response.data ?? {
      items: [],
      pageNumber: Number(params.pageNumber ?? '1') || 1,
      pageSize: Number(params.pageSize ?? '0') || 0,
      totalItems: 0,
      totalPages: 0,
    }
  )
}

export async function getOpenPermits(query: OpenPermitListQuery): Promise<PagedResult<OpenPermitListItem>> {
  const params: Record<string, string | string[]> = {
    tenantId: query.tenantId,
    page: String(query.page ?? 1),
    pageSize: String(query.pageSize ?? 10),
  }

  if (query.siteId) {
    params.siteId = query.siteId
  }

  if (query.activeRoleId && isGuidString(query.activeRoleId)) {
    params.activeRoleId = query.activeRoleId
  }

  if (Array.isArray(query.locationIds) && query.locationIds.length > 0) {
    params.locationIds = query.locationIds.filter(isGuidString)
  }

  if (query.area && query.area.toLowerCase() !== 'all') {
    params.area = query.area
  }

  if (query.permitType && query.permitType.toLowerCase() !== 'all') {
    params.permitType = query.permitType
  }

  if (query.status && query.status.toLowerCase() !== 'all') {
    params.status = query.status
  }

  if (query.searchTerm) {
    params.searchTerm = query.searchTerm
  }

  if (query.plannedStartFrom) {
    params.plannedStartFrom = query.plannedStartFrom
  }

  if (query.plannedStartTo) {
    params.plannedStartTo = query.plannedStartTo
  }

  const response = await axios.get<PagedResult<OpenPermitListItem>>(buildUrl('/api/v1/permits/open'), {
    params,
    paramsSerializer: (requestParams) => {
      const search = new URLSearchParams()
      Object.entries(requestParams).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return
        }
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              search.append(key, String(item))
            }
          })
          return
        }
        search.append(key, String(value))
      })
      return search.toString()
    },
  })

  return (
    response.data ?? {
      items: [],
      pageNumber: Number(params.page) || 1,
      pageSize: Number(params.pageSize) || 0,
      totalItems: 0,
      totalPages: 0,
    }
  )
}

export async function getClosePermits(query: OpenPermitListQuery): Promise<PagedResult<OpenPermitListItem>> {
  const params: Record<string, string | string[]> = {
    tenantId: query.tenantId,
    page: String(query.page ?? 1),
    pageSize: String(query.pageSize ?? 10),
  }

  if (query.siteId) {
    params.siteId = query.siteId
  }

  if (query.activeRoleId && isGuidString(query.activeRoleId)) {
    params.activeRoleId = query.activeRoleId
  }

  if (Array.isArray(query.locationIds) && query.locationIds.length > 0) {
    params.locationIds = query.locationIds.filter(isGuidString)
  }

  if (query.area && query.area.toLowerCase() !== 'all') {
    params.area = query.area
  }

  if (query.permitType && query.permitType.toLowerCase() !== 'all') {
    params.permitType = query.permitType
  }

  if (query.status && query.status.toLowerCase() !== 'all') {
    params.status = query.status
  }

  if (query.searchTerm) {
    params.searchTerm = query.searchTerm
  }

  if (query.plannedStartFrom) {
    params.plannedStartFrom = query.plannedStartFrom
  }

  if (query.plannedStartTo) {
    params.plannedStartTo = query.plannedStartTo
  }

  const response = await axios.get<PagedResult<OpenPermitListItem>>(buildUrl('/api/v1/permits/close'), {
    params,
    paramsSerializer: (requestParams) => {
      const search = new URLSearchParams()
      Object.entries(requestParams).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return
        }
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              search.append(key, String(item))
            }
          })
          return
        }
        search.append(key, String(value))
      })
      return search.toString()
    },
  })

  return (
    response.data ?? {
      items: [],
      pageNumber: Number(params.page) || 1,
      pageSize: Number(params.pageSize) || 0,
      totalItems: 0,
      totalPages: 0,
    }
  )
}

export async function openPermit(
  permitId: string,
  payload: OpenPermitRequestPayload,
): Promise<void> {
  await axios.post(buildUrl(`/api/v1/permits/${permitId}/open`), payload)
}

export async function openPermitsBulk(
  payload: OpenPermitBulkRequestPayload,
): Promise<BulkPermitActionResult> {
  const response = await axios.post<BulkPermitActionResult>(buildUrl('/api/v1/permits/open/bulk'), payload)
  return response.data
}

export async function cancelPermit(
  permitId: string,
  payload: CancelPermitRequestPayload,
): Promise<void> {
  await axios.post(buildUrl(`/api/v1/permits/${permitId}/cancel`), payload)
}

export async function cancelPermitsBulk(
  payload: CancelPermitBulkRequestPayload,
): Promise<BulkPermitActionResult> {
  const response = await axios.post<BulkPermitActionResult>(buildUrl('/api/v1/permits/cancel/bulk'), payload)
  return response.data
}

export async function getCancelReasons(tenantId: string): Promise<CancelReasonOption[]> {
  const response = await axios.get<CancelReasonOption[]>(buildUrl('/api/v1/permits/cancel-reasons'), {
    params: { tenantId },
  })
  return response.data ?? []
}

export interface PermitStatusOption {
  code: string
  descr: string
}

export async function getPermitStatusOptions(tenantId: string): Promise<PermitStatusOption[]> {
  const response = await axios.get<PermitStatusOption[]>(buildUrl('/api/v1/permits/status-options'), {
    params: { tenantId },
  })
  return response.data ?? []
}

export async function closePermit(
  permitId: string,
  payload: ClosePermitRequestPayload,
): Promise<void> {
  await axios.post(buildUrl(`/api/v1/permits/${permitId}/close`), payload)
}

export async function closePermitsBulk(
  payload: ClosePermitBulkRequestPayload,
): Promise<BulkPermitActionResult> {
  const response = await axios.post<BulkPermitActionResult>(buildUrl('/api/v1/permits/close/bulk'), payload)
  return response.data
}

export async function changePermitWorkTime(
  permitId: string,
  payload: ChangePermitWorkTimeRequestPayload,
): Promise<SubmitPermitWorkflowResponse> {
  const normalizedRequestedByUserId = isGuidString(payload.requestedByUserId) ? payload.requestedByUserId : null

  const response = await axios.post<SubmitPermitWorkflowResponse>(
    buildUrl(`/api/v1/permits/${permitId}/change-work-time`),
    {
      ...payload,
      requestedByUserId: normalizedRequestedByUserId,
    },
  )

  return response.data
}

export async function extendPermit(
  permitId: string,
  payload: ExtendPermitRequestPayload,
): Promise<SubmitPermitWorkflowResponse> {
  const normalizedRequestedByUserId = isGuidString(payload.requestedByUserId) ? payload.requestedByUserId : null

  const response = await axios.post<SubmitPermitWorkflowResponse>(
    buildUrl(`/api/v1/permits/${permitId}/extend`),
    {
      ...payload,
      requestedByUserId: normalizedRequestedByUserId,
    },
  )

  return response.data
}

export async function searchPermitSupervisors(
  tenantId?: string,
  search?: string,
  page = 1,
  pageSize = 10,
): Promise<PagedResult<PermitEmployeeLookup>> {
  const params: Record<string, string | number> = {
    page,
    pageSize,
  }

  if (tenantId && tenantId.trim()) {
    params.tenantId = tenantId.trim()
  }
  if (search && search.trim()) {
    params.search = search.trim()
  }

  const response = await axios.get<PagedResult<PermitEmployeeLookup>>(buildUrl('/api/v1/permits/lookup/employees'), {
    params,
  })

  return (
    response.data ?? {
      items: [],
      pageNumber: page,
      pageSize,
      totalItems: 0,
      totalPages: 0,
    }
  )
}

export async function getPermitDetail(tenantId: string, permitId: string): Promise<PermitDetail | null> {
  const response = await axios.get<PermitDetail>(buildUrl(`/api/v1/permits/${permitId}`), {
    params: { tenantId },
  })

  return response.data ?? null
}

export async function updatePermit(
  permitId: string,
  payload: UpdatePermitRequestPayload,
): Promise<CreatePermitResponsePayload> {
  const response = await axios.post<CreatePermitResponsePayload>(buildUrl(`/api/v1/permits/${permitId}/save`), payload)
  return response.data
}

export async function uploadPermitAttachment(formData: FormData): Promise<UploadAttachmentResponse> {
  const response = await axios.post<UploadAttachmentResponse>(buildUrl('/api/v1/attachments/upload'), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return response.data
}

export async function getActiveDocuments(tenantId: string): Promise<DocumentSummary[]> {
  const response = await axios.get<DocumentSummary[]>(buildUrl('/api/v1/documents'), {
    params: { tenantId },
  })

  return response.data ?? []
}

export async function submitPermitWorkflow(
  permitId: string,
  tenantId: string,
  initiatedBy?: string | null,
): Promise<SubmitPermitWorkflowResponse> {
  const normalizedInitiatedBy = isGuidString(initiatedBy) ? initiatedBy : null

  const response = await axios.post<SubmitPermitWorkflowResponse>(buildUrl(`/api/v1/permits/${permitId}/submit`), {
    tenantId,
    permitId,
    initiatedBy: normalizedInitiatedBy,
  })

  return response.data
}

export async function validatePermitSubmitPolicy(
  permitId: string,
  tenantId: string,
  initiatedBy?: string | null,
  userType?: string | null,
): Promise<PermitSubmitPolicyValidationResult> {
  const normalizedInitiatedBy = isGuidString(initiatedBy) ? initiatedBy : null
  const normalizedUserType =
    typeof userType === 'string' && userType.trim().length > 0 ? userType.trim() : null

  const response = await axios.get<PermitSubmitPolicyValidationResult>(
    buildUrl(`/api/v1/permits/${permitId}/submit-policy-validation`),
    {
      params: {
        tenantId,
        initiatedBy: normalizedInitiatedBy,
        userType: normalizedUserType,
      },
    },
  )

  return (
    response.data ?? {
      canSubmit: true,
      policyFound: false,
      policyId: null,
      errors: [],
      warnings: [],
    }
  )
}

export async function getPermitWorkflowTimeline(
  tenantId: string,
  permitId: string,
): Promise<PermitWorkflowTimeline | null> {
  const response = await axios.get<PermitWorkflowTimeline>(
    buildUrl(`/api/v1/permits/${permitId}/workflow/timeline`),
    {
      params: { tenantId },
    },
  )

  return response.data ?? null
}

export async function getUserById(userId: string, tenantId?: string): Promise<UserSummary | null> {
  if (!isGuidString(userId)) {
    return null
  }

  try {
    const response = await axios.get<UserSummary>(buildUrl(`/api/v1/users/${userId}`), {
      params: tenantId ? { tenantId } : undefined,
    })
    return response.data ?? null
  } catch (error) {
    // Swallow errors; caller will fallback to other name sources
    console.error("Failed to fetch user by id", userId, error)
    return null
  }
}

export interface ApprovePermitStageRequest {
  tenantId: string
  permitId: string
  stageId: string
  actedByUserId?: string | null
  actionNote?: string | null
}

export interface RejectPermitStageRequest extends ApprovePermitStageRequest {
  actionNote: string
}

export interface RequestReviewPermitStageRequest extends ApprovePermitStageRequest {
  actionNote: string
}

export interface WorkflowActionResponse {
  permitId: string
  stageId: string
  success?: boolean | null
  message?: string | null
  errorMessage?: string | null
  warningMessage?: string | null
  warnings?: Array<{ message?: string | null } | string> | null
  nextApproverFound?: boolean | null
  emailErrorMessage?: string | null
}

export async function approvePermitStage(request: ApprovePermitStageRequest): Promise<WorkflowActionResponse> {
  const payload = {
    tenantId: request.tenantId,
    permitId: request.permitId,
    stageId: request.stageId,
    actedByUserId: request.actedByUserId,
    actionNote: request.actionNote,
  }

  const response = await axios.post<WorkflowActionResponse>(buildUrl(`/api/v1/permits/${request.permitId}/workflow/approve`), payload)
  return response.data
}

export async function rejectPermitStage(request: RejectPermitStageRequest): Promise<WorkflowActionResponse> {
  const payload = {
    tenantId: request.tenantId,
    permitId: request.permitId,
    stageId: request.stageId,
    actedByUserId: request.actedByUserId,
    actionNote: request.actionNote,
  }

  const response = await axios.post<WorkflowActionResponse>(buildUrl(`/api/v1/permits/${request.permitId}/workflow/reject`), payload)
  return response.data
}

export async function requestReviewPermitStage(request: RequestReviewPermitStageRequest): Promise<WorkflowActionResponse> {
  const payload = {
    tenantId: request.tenantId,
    permitId: request.permitId,
    stageId: request.stageId,
    actedByUserId: request.actedByUserId,
    actionNote: request.actionNote,
  }

  const response = await axios.post<WorkflowActionResponse>(buildUrl(`/api/v1/permits/${request.permitId}/workflow/request-review`), payload)
  return response.data
}

export interface NavBadgeCountsRequest {
  tenantId: string
  userId?: string
  roleIds?: string[]
}

export interface NavBadgeCountsResponse {
  pendingApprovalsCount: number
  openPermitsCount: number
  openPermitsByRole: Record<string, number>
  closePermitsCount: number
  closePermitsByRole: Record<string, number>
  contractorWaitingCount: number
}

export async function getNavBadgeCounts(request: NavBadgeCountsRequest): Promise<NavBadgeCountsResponse> {
  const payload = {
    tenantId: request.tenantId,
    userId: request.userId || undefined,
    roleIds: request.roleIds || undefined,
  }

  const response = await axios.post<NavBadgeCountsResponse>(buildUrl('/api/v1/permits/nav-badge-counts'), payload)
  return response.data
}

export interface MassApproveResultItem {
  permitId: string
  success: boolean
  errorMessage?: string | null
}

export interface MassApproveResponse {
  results: MassApproveResultItem[]
  succeededCount: number
  failedCount: number
}

export async function massApprovePermits(
  tenantId: string,
  actedByUserId: string,
  permitIds: string[],
  actionNote?: string,
): Promise<MassApproveResponse> {
  const response = await axios.post<MassApproveResponse>(buildUrl('/api/v1/permits/mass-approve'), {
    tenantId,
    actedByUserId,
    permitIds,
    actionNote: actionNote ?? null,
  })
  return response.data ?? { results: [], succeededCount: 0, failedCount: 0 }
}

export async function massRejectPermits(
  tenantId: string,
  actedByUserId: string,
  permitIds: string[],
  actionNote: string,
): Promise<MassApproveResponse> {
  const response = await axios.post<MassApproveResponse>(buildUrl('/api/v1/permits/mass-reject'), {
    tenantId,
    actedByUserId,
    permitIds,
    actionNote,
  })
  return response.data ?? { results: [], succeededCount: 0, failedCount: 0 }
}

export async function getPendingApprovalsCount(tenantId: string, userId?: string): Promise<number> {
  const params: Record<string, string> = { tenantId }
  if (userId) params.userId = userId
  const response = await axios.get<number>(buildUrl('/api/v1/permits/pending-approvals-count'), { params })
  return response.data
}

export async function getContractorWaitingCount(tenantId: string, userId?: string): Promise<number> {
  const params: Record<string, string> = { tenantId }
  if (userId) params.userId = userId
  const response = await axios.get<number>(buildUrl('/api/v1/permits/contractor-waiting-count'), { params })
  return response.data
}
